import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import logoZofia from '@/assets/logo-zofia.png';
import SmartLink from '@/components/SmartLink';
import { isPromoActive } from '@/lib/promo';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const promoActive = isPromoActive();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const servicesLinks = [
    {
      to: '/marketing-agency',
      title: 'Marketing Agency',
      desc: 'Farti notare, scegliere e vendere.',
    },
    {
      to: '/digital-solutions',
      title: 'Digital Solutions',
      desc: 'Farla girare meglio e più in fretta.',
    },
  ];

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'py-2 bg-background/60 backdrop-blur-lg border-b border-gold/10'
            : 'py-4 bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        <div className="container-premium flex items-center justify-between">
          <Link to="/" className="relative z-50" aria-label="Torna alla home">
            <img
              src={logoZofia}
              alt="Zofia Digital Agency"
              width={112}
              height={112}
              className={`w-auto transition-all duration-500 transform-gpu origin-left scale-[1.28] ${
                isScrolled ? 'h-12 md:h-14' : 'h-16 md:h-20'
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {promoActive && (
              <Link
                to="/promozioni"
                className="promo-nav-pill group relative text-[11px] font-semibold uppercase tracking-[0.18em] text-gold px-3.5 py-1.5 rounded-full border border-gold/40 bg-gold/5 hover:bg-gold/10 hover:text-gold-light transition-colors"
              >
                <span className="relative z-10">Promo</span>
                <span className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-gold/30 animate-promo-glow" aria-hidden="true" />
              </Link>
            )}

            {/* Servizi dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <Link
                to="/servizi"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-300 inline-flex items-center gap-1"
              >
                Servizi
                <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
              </Link>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[520px]"
                  >
                    <div className="glass-card border-gold/20 p-3 grid grid-cols-2 gap-2">
                      {servicesLinks.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          className="group p-4 rounded-lg hover:bg-gold/5 border border-transparent hover:border-gold/20 transition-all"
                        >
                          <p className="font-serif text-base group-hover:text-gold transition-colors mb-1">
                            {s.title}
                          </p>
                          <p className="text-xs text-muted-foreground">{s.desc}</p>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/insights"
              className="text-sm font-medium text-muted-foreground hover:text-foreground link-underline transition-colors duration-300"
            >
              Insights
            </Link>

            <SmartLink
              href="#perche-noi"
              className="text-sm font-medium text-muted-foreground hover:text-foreground link-underline transition-colors duration-300"
            >
              Perché Noi
            </SmartLink>

            <Link
              to="/contatti"
              className="text-sm font-medium text-muted-foreground hover:text-foreground link-underline transition-colors duration-300"
            >
              Contatti
            </Link>
          </nav>

          <div className="hidden md:block">
            <Link to="/contatti" className="btn-premium text-sm">
              <span>Parliamone</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden relative z-50 w-10 h-10 flex flex-col items-center justify-center gap-1.5"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <motion.span className="w-6 h-0.5 bg-foreground block" animate={{ rotate: isMobileMenuOpen ? 45 : 0, y: isMobileMenuOpen ? 6 : 0 }} />
            <motion.span className="w-6 h-0.5 bg-foreground block" animate={{ opacity: isMobileMenuOpen ? 0 : 1 }} />
            <motion.span className="w-6 h-0.5 bg-foreground block" animate={{ rotate: isMobileMenuOpen ? -45 : 0, y: isMobileMenuOpen ? -6 : 0 }} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-40 bg-background flex items-center justify-center overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <nav className="flex flex-col items-center gap-6 py-24">
              {promoActive && (
                <Link
                  to="/promozioni"
                  className="text-sm font-semibold uppercase tracking-[0.22em] text-gold px-5 py-2 rounded-full border border-gold/40 bg-gold/5"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Promo
                </Link>
              )}
              <Link to="/marketing-agency" className="text-2xl font-serif text-foreground hover:text-gold transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                Marketing Agency
              </Link>
              <Link to="/digital-solutions" className="text-2xl font-serif text-foreground hover:text-gold transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                Digital Solutions
              </Link>
              <Link to="/insights" className="text-2xl font-serif text-foreground hover:text-gold transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                Insights
              </Link>
              <SmartLink href="#perche-noi" className="text-2xl font-serif text-foreground hover:text-gold transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                Perché Noi
              </SmartLink>
              <Link to="/contatti" className="text-2xl font-serif text-foreground hover:text-gold transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                Contatti
              </Link>
              <Link to="/contatti" className="btn-premium mt-4" onClick={() => setIsMobileMenuOpen(false)}>
                <span>Parliamone</span>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
